import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "liveRegion", "wrapper"]
  static values = {
    url: String,
    minChars: { type: Number, default: 2 },
    debounce: { type: Number, default: 500 }
  }

  connect() {
    this.activeIndex = -1
    this.abortController = null
    this.debounceTimer = null
    this.boundClose = this.closeOnOutsideClick.bind(this)
    this.boundSlash = this.handleSlashFocus.bind(this)
    document.addEventListener("click", this.boundClose)
    document.addEventListener("keydown", this.boundSlash)
  }

  disconnect() {
    this.cancelFetch()
    clearTimeout(this.debounceTimer)
    document.removeEventListener("click", this.boundClose)
    document.removeEventListener("keydown", this.boundSlash)
  }

  onInput() {
    const query = this.inputTarget.value.trim()
    clearTimeout(this.debounceTimer)

    if (query.length < this.minCharsValue) {
      this.close()
      if (query.length > 0) {
        this.showMinChars()
      }
      return
    }

    this.showLoading()
    this.debounceTimer = setTimeout(() => this.fetchResults(query), this.debounceValue)
  }

  async fetchResults(query) {
    this.cancelFetch()
    this.abortController = new AbortController()

    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
      const response = await fetch(url, {
        headers: {
          "Accept": "text/html",
          "Turbo-Frame": "search-results"
        },
        signal: this.abortController.signal
      })

      if (response.status === 429) {
        this.showError(this.resultsTarget.dataset.rateLimitedMsg)
        return
      }

      if (!response.ok) {
        this.showError(this.resultsTarget.dataset.errorMsg)
        return
      }

      const html = await response.text()
      this.resultsTarget.innerHTML = html
      this.open()
      this.activeIndex = -1
      this.announceResults()
    } catch (error) {
      if (error.name !== "AbortError") {
        this.showError(this.resultsTarget.dataset.errorMsg)
      }
    }
  }

  select(event) {
    // Navigation handled by link click
  }

  onKeydown(event) {
    const options = this.resultOptions()
    if (!options.length) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.activeIndex = Math.min(this.activeIndex + 1, options.length - 1)
        this.highlightOption(options)
        break
      case "ArrowUp":
        event.preventDefault()
        this.activeIndex = Math.max(this.activeIndex - 1, 0)
        this.highlightOption(options)
        break
      case "Enter":
        event.preventDefault()
        if (this.activeIndex >= 0 && options[this.activeIndex]) {
          options[this.activeIndex].click()
        }
        break
      case "Escape":
        event.preventDefault()
        this.close()
        this.inputTarget.blur()
        if (this.hasWrapperTarget) {
          this.wrapperTarget.removeAttribute("data-expanded")
        }
        break
    }
  }

  expandMobile() {
    if (this.hasWrapperTarget) {
      this.wrapperTarget.setAttribute("data-expanded", "true")
      this.inputTarget.focus()
    }
  }

  // Private

  resultOptions() {
    return [...this.resultsTarget.querySelectorAll("[role='option']")]
  }

  highlightOption(options) {
    options.forEach((opt, i) => {
      opt.classList.toggle("bg-white/20", i === this.activeIndex)
      if (i === this.activeIndex) {
        this.inputTarget.setAttribute("aria-activedescendant", opt.id)
      }
    })
  }

  announceResults() {
    if (!this.hasLiveRegionTarget) return
    const count = this.resultOptions().length
    this.liveRegionTarget.textContent = count > 0
      ? `${count} ${count === 1 ? this.liveRegionTarget.dataset.singularLabel : this.liveRegionTarget.dataset.pluralLabel}`
      : this.liveRegionTarget.dataset.noResultsLabel
  }

  open() {
    this.resultsTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  close() {
    this.resultsTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
    this.activeIndex = -1
  }

  showLoading() {
    this.showMessage(this.resultsTarget.dataset.loadingMsg, "text-muted")
  }

  showMinChars() {
    this.showMessage(this.resultsTarget.dataset.minCharsMsg, "text-muted text-xs")
  }

  showError(message) {
    this.showMessage(message, "text-ruby-500")
  }

  showMessage(text, cssClass) {
    const div = document.createElement("div")
    div.className = `px-3 py-4 text-center text-sm ${cssClass}`
    div.textContent = text
    this.resultsTarget.replaceChildren(div)
    this.open()
  }

  cancelFetch() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  handleSlashFocus(event) {
    if (event.key === "/" && !this.isInputFocused()) {
      event.preventDefault()
      this.inputTarget.focus()
    }
  }

  isInputFocused() {
    const tag = document.activeElement?.tagName
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || document.activeElement?.isContentEditable
  }
}
